import { useState, useEffect } from 'react';
import { ArrowLeft, Trophy, Users } from 'lucide-react';
import { useNavigate } from 'react-router';

import { formatNumber } from '@/utils/format';

interface CampaignHeaderProps {
  prizePool: string;
  endDate: string;
  userPoints: number;
  caps: {
    total: number;
    platform: number;
    twitter: number;
    special: number;
  };
  totalParticipants: number;
  totalPoints: number;
  phase: 'before' | 'early_access' | 'active' | 'ended';
}

export function CampaignHeader({
  prizePool,
  endDate,
  userPoints,
  caps,
  totalParticipants,
  totalPoints,
  phase,
}: CampaignHeaderProps) {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Parse prize pool from lamports to SOL
  const prizePoolSol = parseInt(prizePool) / 1e9;

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(endDate).getTime();
      const distance = target - now;

      if (distance < 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [endDate]);

  const formatCountdown = () => {
    const { days, hours, minutes, seconds } = timeLeft;
    return `${days}D ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  return (
    <div className="bg-gradient-to-b from-cyan-900/20 to-transparent px-4 py-6 mb-4">
      <div className="container mx-auto">
        {/* Back button and title row */}
        <div className="flex items-start gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-cyan-400/20 hover:bg-cyan-400/30 transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-cyan-400" />
          </button>

          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-white leading-tight">
              Join the New Year Campaign and Split the{' '}
              <span className="text-cyan-400">{prizePoolSol} SOL</span> Prize Pool!
            </h1>
          </div>

          {/* Draw info and user points - desktop */}
          <div className="hidden md:flex flex-col items-end gap-2">
            <button className="px-6 py-2 rounded-full bg-gray-800 border border-gray-700 text-white font-semibold hover:bg-gray-700 transition-colors">
              <Trophy className="w-4 h-4 inline-block mr-2 text-cyan-400" />
              Win the Draw
            </button>
            <p className="text-gray-400 text-sm">
              Campaign ends in: <span className="text-white font-mono">{formatCountdown()}</span>
            </p>
          </div>
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Mobile draw button */}
          <div className="md:hidden w-full flex flex-col items-center gap-2 mb-4">
            <button className="px-6 py-2 rounded-full bg-gray-800 border border-gray-700 text-white font-semibold hover:bg-gray-700 transition-colors">
              <Trophy className="w-4 h-4 inline-block mr-2 text-cyan-400" />
              Win the Draw
            </button>
            <p className="text-gray-400 text-sm">
              Campaign ends in: <span className="text-white font-mono">{formatCountdown()}</span>
            </p>
          </div>

          {/* User points */}
          <div className="flex flex-col items-start">
            <span className="text-gray-400 text-sm">Your points</span>
            <span className="text-3xl md:text-4xl font-bold text-cyan-400">
              {formatNumber(userPoints)} Points
            </span>
          </div>

          {/* Total stats */}
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <Users className="w-4 h-4" />
              <span>{formatNumber(totalParticipants)} participants</span>
            </div>
            <span className="text-gray-400 text-sm">
              All users&apos; total points
            </span>
            <span className="text-lg font-semibold text-white">
              {formatNumber(totalPoints)} Points
            </span>
          </div>
        </div>

        {/* Early access badge */}
        {phase === 'early_access' && (
          <div className="mt-4 inline-flex items-center px-3 py-1 rounded-full bg-purple-500/20 border border-purple-500/50 text-purple-300 text-sm">
            <span className="w-2 h-2 rounded-full bg-purple-400 mr-2 animate-pulse"></span>
            Early Access Active
          </div>
        )}
      </div>
    </div>
  );
}
